import {
  isPerformanceSupportReason,
  type PerformanceSupportReason,
} from '../types/performanceSupport';

const LISTED_REASONS = new Set<PerformanceSupportReason>([
  'exercise_pain',
  'exercise_substitution',
  'video_correction',
  'questions',
  'workout_adjustment',
  'became_pregnant',
]);

export interface PerformanceSupportDashboardQuery {
  searchName: string;
  reasons: PerformanceSupportReason[];
  page: number;
  pageSize: number;
}

export type PerformanceSupportResponse = 'yes' | 'no' | 'none';

export interface PerformanceSupportDashboardRow {
  id: number;
  studentId: number;
  studentName: string;
  coachName: string | null;
  phoneCountryCode: string | null;
  phoneNumber: string | null;
  reason: PerformanceSupportReason;
  createdAt: string;
  respondedAt: string | null;
  response: PerformanceSupportResponse | null;
}

export interface PerformanceSupportDashboard {
  page: number;
  pageSize: number;
  totalCount: number;
  students: PerformanceSupportDashboardRow[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readPositiveInt(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isInteger(value) || value <= 0) {
    return null;
  }

  return value;
}

function readText(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed || null;
}

function parseRow(value: unknown): PerformanceSupportDashboardRow | null {
  if (!isRecord(value)) {
    return null;
  }

  const id = readPositiveInt(value.id);
  const studentId = readPositiveInt(value.studentId);
  const studentName = readText(value.studentName);
  const reason = readText(value.reason);
  const createdAt = readText(value.createdAt);
  const respondedAt = value.respondedAt == null ? null : readText(value.respondedAt);
  const response = readResponse(value.response);
  if (
    id === null
    || studentId === null
    || !studentName
    || !reason
    || !createdAt
    || !isPerformanceSupportReason(reason)
    || !LISTED_REASONS.has(reason)
    || (value.respondedAt != null && !respondedAt)
    || response === undefined
  ) {
    return null;
  }

  return {
    id,
    studentId,
    studentName,
    coachName: readText(value.coachName),
    phoneCountryCode: readText(value.phoneCountryCode),
    phoneNumber: readText(value.phoneNumber),
    reason,
    createdAt,
    respondedAt,
    response,
  };
}

function readResponse(value: unknown): PerformanceSupportResponse | null | undefined {
  if (value == null || value === '') {
    return null;
  }

  if (value === 'yes' || value === 'no' || value === 'none') {
    return value;
  }

  return undefined;
}

export function parsePerformanceSupportDashboard(body: unknown): PerformanceSupportDashboard | null {
  if (!isRecord(body) || !Array.isArray(body.students) || typeof body.totalCount !== 'number') {
    return null;
  }

  const page = readPositiveInt(body.page) ?? 1;
  const pageSize = readPositiveInt(body.pageSize) ?? 20;
  const students = body.students
    .map((row) => parseRow(row))
    .filter((row): row is PerformanceSupportDashboardRow => row !== null);

  if (!Number.isInteger(body.totalCount) || body.totalCount < 0) {
    return null;
  }

  return {
    page,
    pageSize,
    totalCount: body.totalCount,
    students,
  };
}
